import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  Users, 
  Building2, 
  ShoppingBag, 
  Home,
  CreditCard,
  BarChart3,
  Settings,
  Shield,
  ArrowLeft,
  FileText,
  UserCog,
  Ticket,
  Webhook,
  AlertTriangle,
  Activity,
  Plug,
  UserX,
  ShieldAlert,
  ClipboardCheck,
  Menu,
  HandCoins,
  Palette,
  Newspaper,
  Key,
  UserRound
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { AdminNotificationBell } from "./AdminNotificationBell";
import { useSafeAreaInsets } from "@/hooks/useSafeAreaInsets";

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
  { icon: UserX, label: "Abandonment", path: "/admin/abandonment" },
  { icon: BarChart3, label: "Analytics", path: "/admin/analytics" },
  { icon: Plug, label: "API Status", path: "/admin/api-status" },
  { icon: Newspaper, label: "Blog", path: "/admin/blog" },
  { icon: Key, label: "Blog API", path: "/admin/blog-api" },
  { icon: Palette, label: "Brand Kit", path: "/admin/brand-kit" },
  { icon: ShoppingBag, label: "Buyers", path: "/admin/buyers" },
  { icon: FileText, label: "Contracts", path: "/admin/contracts" },
  { icon: Activity, label: "Edge Function Logs", path: "/admin/edge-logs" },
  { icon: UserRound, label: "Homeowners", path: "/admin/homeowners" },
  { icon: ClipboardCheck, label: "Maintenance", path: "/admin/maintenance" },
  { icon: HandCoins, label: "Offers", path: "/admin/offers" },
  { icon: Ticket, label: "Promo Codes", path: "/admin/promo-codes" },
  { icon: Home, label: "Properties", path: "/admin/properties" },
  { icon: ShieldAlert, label: "Rate Limits", path: "/admin/rate-limits" },
  { icon: Building2, label: "Sellers", path: "/admin/sellers" },
  { icon: Settings, label: "Settings", path: "/admin/settings" },
  { icon: CreditCard, label: "Stripe Config", path: "/admin/stripe-config" },
  { icon: CreditCard, label: "Subscriptions", path: "/admin/subscriptions" },
  { icon: UserCog, label: "User Management", path: "/admin/user-management" },
  { icon: Users, label: "Users", path: "/admin/users" },
  { icon: FileText, label: "Valuation Leads", path: "/admin/leads" },
  { icon: AlertTriangle, label: "Webhook Alerts", path: "/admin/alerts" },
  { icon: Webhook, label: "Webhook Events", path: "/admin/webhooks" },
];

export const AdminMobileNav = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { topPadding, topMargin } = useSafeAreaInsets();

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Mobile Header Bar */}
      <header className={cn("fixed top-0 left-0 right-0 z-50 h-14 bg-card border-b border-border flex items-center justify-between px-4", topPadding)}>
        <div className="flex items-center gap-3">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 bg-card">
              <SheetHeader className="p-4 border-b border-border">
                <SheetTitle className="flex items-center gap-2 text-godyspo-gold">
                  <Shield className="h-5 w-5" />
                  Admin Console
                </SheetTitle>
              </SheetHeader>
              
              <nav className="flex flex-col h-[calc(100vh-65px)] overflow-y-auto">
                <div className="flex-1 p-2 space-y-1">
                  {navItems.map((item) => (
                    <Link 
                      key={item.path} 
                      to={item.path}
                      onClick={() => setOpen(false)}
                    >
                      <Button
                        variant="ghost"
                        className={cn(
                          "w-full justify-start h-11",
                          isActive(item.path) && "bg-godyspo-gold/10 text-godyspo-gold hover:bg-godyspo-gold/20"
                        )}
                      >
                        <item.icon className="mr-3 h-4 w-4" />
                        {item.label}
                      </Button>
                    </Link>
                  ))}
                </div>
                
                <div className="p-2 border-t border-border">
                  <Link to="/dashboard" onClick={() => setOpen(false)}>
                    <Button
                      variant="ghost"
                      className="w-full justify-start h-11 text-muted-foreground hover:text-foreground"
                    >
                      <ArrowLeft className="mr-3 h-4 w-4" />
                      Seller Dashboard
                    </Button>
                  </Link>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
          
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-godyspo-gold" />
            <span className="font-semibold text-godyspo-gold">Admin</span>
          </div>
        </div>
        
        <AdminNotificationBell />
      </header>
      
      {/* Spacer for fixed header */}
      <div className={cn("h-14", topMargin)} />
    </>
  );
};
